import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { forkJoin, of } from "rxjs";
import { OmniarkUiModule } from "@corp-ais/omniark-ui";
import { DateTimePickerComponent } from "@corp-ais/omniark-ui/date-time-picker";
import { ProductOfferingService } from "../../../../services/enrichment-data.service";
import {
  BANGKOK_ZONE_LABEL,
  formatScheduledAt,
  isFutureSchedule,
  toScheduledAt,
} from "@app/shared/utils/publish-schedule.util";
import { EnrichmentPriceRow } from "../enrichment-list.types";
import {
  NO_OPEN_SCHEDULES,
  OpenScheduleIndex,
  OpenScheduleLoader,
  openScheduleOf,
} from "../open-schedules";
import {
  LIFECYCLE_ACTIONS,
  LifecycleAction,
  planTransitions,
  plannedPriceCount,
  scheduleDirection,
  TransitionPlan,
  TransitionSkip,
} from "../price-transitions";
import { readPriceRows } from "../price-rows";
import { outcomeSummary, TransitionOutcome } from "../transition-requests";

/** What the user confirmed: the plan on screen, and for a schedule the RFC
 *  3339 time ("" otherwise). */
export interface TransitionConfirmation {
  plan: TransitionPlan;
  scheduledAt: string;
}

/** The selection as the drawer re-read it. `gone` holds the selected rows
 *  the list no longer has. */
export interface TransitionRowsRefresh {
  rows: readonly EnrichmentPriceRow[];
  gone: readonly EnrichmentPriceRow[];
}

interface PlanBasis extends TransitionRowsRefresh {
  openSchedules: OpenScheduleIndex;
}

interface PlannedGroup {
  offeringId: string;
  offeringName: string;
  prices: EnrichmentPriceRow[];
  outcome?: TransitionOutcome;
}

interface SkipLine {
  key: string;
  title: string;
  offeringName: string;
  why: string;
}

/**
 * The one confirm step for every lifecycle action, bulk or row (a row action
 * is a selection of one). It re-reads the selected prices, plans the
 * requests with `planTransitions`, says how many selected prices are
 * eligible and why the rest are not, takes the time for a schedule, and
 * shows each package's result. The host sends the requests, so closing the
 * drawer never cancels a write in flight.
 */
@Component({
  selector: "app-enrichment-transition-drawer",
  standalone: true,
  imports: [OmniarkUiModule, DateTimePickerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./enrichment-transition-drawer.html",
})
export class EnrichmentTransitionDrawer implements OnInit {
  private readonly productOfferingService = inject(ProductOfferingService);
  private readonly openScheduleLoader = inject(OpenScheduleLoader);
  private readonly destroyRef = inject(DestroyRef);

  readonly action = input.required<LifecycleAction>();
  readonly rows = input.required<readonly EnrichmentPriceRow[]>();
  readonly channel = input.required<string>();
  readonly channelLabel = input("");
  readonly sending = input(false);
  readonly outcomes = input<readonly TransitionOutcome[] | null>(null);

  readonly confirmed = output<TransitionConfirmation>();
  readonly closed = output<void>();
  readonly refreshed = output<TransitionRowsRefresh>();

  protected readonly zoneLabel = BANGKOK_ZONE_LABEL;
  protected readonly spec = computed(() => LIFECYCLE_ACTIONS[this.action()]);
  protected readonly isSchedule = computed(
    () => scheduleDirection(this.action()) !== null,
  );

  /** Null while reading. A selected row's listed status may be stale. It can
   *  sit on another page, or someone changed the price since. */
  protected readonly basis = signal<PlanBasis | null>(null);
  protected readonly readFailed = signal(false);
  protected readonly scheduledAt = signal("");
  /** Read at each pick and at confirm. A time can pass while the drawer is
   *  open, and a computed that read the clock itself would keep its first
   *  answer. */
  private readonly now = signal(Date.now());

  protected readonly plan = computed(() => {
    const basis = this.basis();
    return planTransitions(
      basis?.rows ?? [],
      this.action(),
      basis?.openSchedules ?? NO_OPEN_SCHEDULES,
    );
  });
  protected readonly eligibleCount = computed(() =>
    plannedPriceCount(this.plan()),
  );
  protected readonly selectedCount = computed(
    () => new Set(this.rows().map((row) => row.priceId)).size,
  );
  protected readonly result = computed(() =>
    outcomeSummary(this.action(), this.outcomes() ?? []),
  );

  protected readonly groups = computed<PlannedGroup[]>(() => {
    const byPrice = new Map(
      (this.basis()?.rows ?? []).map((row) => [row.priceId, row]),
    );
    const outcomes = new Map(
      (this.outcomes() ?? []).map((o) => [o.offeringId, o]),
    );
    return [...this.plan().requests].map(([offeringId, priceIds]) => ({
      offeringId,
      offeringName: this.offeringName(offeringId),
      prices: priceIds
        .map((id) => byPrice.get(id))
        .filter((row): row is EnrichmentPriceRow => !!row),
      outcome: outcomes.get(offeringId),
    }));
  });

  protected readonly blocked = computed<SkipLine[]>(() => [
    ...this.plan()
      .skipped.filter((skip) => skip.reason !== "duplicate")
      .map((skip) => this.skipLine(skip)),
    ...(this.basis()?.gone ?? []).map((row) => ({
      key: row.key,
      title: row.title,
      offeringName: this.offeringName(row.offeringId),
      why: `No longer listed on ${this.channelLabel() || "this channel"}`,
    })),
  ]);

  protected readonly shared = computed<SkipLine[]>(() =>
    this.plan()
      .skipped.filter((skip) => skip.reason === "duplicate")
      .map((skip) => this.skipLine(skip)),
  );

  protected readonly timeIsFuture = computed(() =>
    isFutureSchedule(this.scheduledAt(), new Date(this.now())),
  );

  protected readonly canConfirm = computed(
    () =>
      !this.sending() &&
      this.outcomes() === null &&
      this.eligibleCount() > 0 &&
      (!this.isSchedule() || this.timeIsFuture()),
  );

  ngOnInit(): void {
    const listed = this.rows();
    forkJoin({
      // A selection can mix rows ticked in the live view with rows ticked
      // under "Only deleted packages", so no single list view covers it.
      fresh: readPriceRows(
        this.productOfferingService,
        listed.map((row) => row.priceId),
        this.channel(),
        { deleted: "include" },
      ),
      openSchedules: this.isSchedule()
        ? this.openScheduleLoader.load(listed, this.channel())
        : of(NO_OPEN_SCHEDULES),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ fresh, openSchedules }) => {
          const byKey = new Map(fresh.map((row) => [row.key, row]));
          const refresh: TransitionRowsRefresh = {
            rows: listed.flatMap((row) => byKey.get(row.key) ?? []),
            gone: listed.filter((row) => !byKey.has(row.key)),
          };
          this.basis.set({ ...refresh, openSchedules });
          this.refreshed.emit(refresh);
        },
        error: () => this.readFailed.set(true),
      });
  }

  protected pickTime(value: string): void {
    this.now.set(Date.now());
    this.scheduledAt.set(value);
  }

  protected confirm(): void {
    this.now.set(Date.now());
    if (!this.canConfirm()) return;
    this.confirmed.emit({
      plan: this.plan(),
      scheduledAt: this.isSchedule() ? toScheduledAt(this.scheduledAt()) : "",
    });
  }

  protected close(): void {
    this.closed.emit();
  }

  private offeringName(offeringId: string): string {
    const row = this.rows().find((r) => r.offeringId === offeringId);
    return row?.offeringName || offeringId;
  }

  private skipLine(skip: TransitionSkip): SkipLine {
    const row =
      this.rows().find(
        (r) => r.priceId === skip.priceId && r.offeringId === skip.offeringId,
      ) ?? this.rows().find((r) => r.priceId === skip.priceId);
    return {
      key: `${skip.offeringId}::${skip.priceId}`,
      title: row?.title ?? skip.priceId,
      offeringName: this.offeringName(skip.offeringId),
      why: this.skipReason(skip),
    };
  }

  private skipReason(skip: TransitionSkip): string {
    switch (skip.reason) {
      case "notOnChannel":
        return `Not enriched on ${this.channelLabel() || "this channel"} yet`;
      case "status":
        return `Not ${this.spec().requires}`;
      case "alreadyScheduled": {
        const direction = scheduleDirection(this.action()) ?? "publish";
        const open = openScheduleOf(
          this.basis()?.openSchedules ?? NO_OPEN_SCHEDULES,
          skip.priceId,
          direction,
        );
        const what = direction === "unpublish" ? "take-down" : "publish";
        return open
          ? `Already has a ${what} scheduled for ${formatScheduledAt(open.scheduledAt)}`
          : `Already has a ${what} scheduled`;
      }
      case "duplicate": {
        const carrier = [...this.plan().requests].find(([, ids]) =>
          ids.includes(skip.priceId),
        )?.[0];
        return `Changes once, under ${carrier ? this.offeringName(carrier) : "another product offering"}`;
      }
    }
  }
}
